import { useAuth } from '@/contexts/AuthContext'
import type { ReactNode } from 'react'

/**
 * True when the current user's JWT carries the given scope.
 *
 * For gating a callback or prop rather than rendered children — e.g. only
 * handing a grid's Restore action to users the backend would accept.
 */
export function useHasScope(scope: string): boolean {
  const { user } = useAuth()
  return user?.scopes?.includes(scope) ?? false
}

interface RequireScopeProps {
  /** Scope the user must have for the children to render (e.g. 'order_management'). */
  scope: string
  children: ReactNode
  /** Optional content to render when the user lacks the scope. Defaults to nothing. */
  fallback?: ReactNode
}

/**
 * Renders its children only if the current user has the given scope.
 *
 * Scopes come from the JWT and mirror what the backend enforces — this guard is
 * UX only; the backend is the real access-control boundary.
 */
export function RequireScope({
  scope,
  children,
  fallback = null,
}: RequireScopeProps) {
  const hasScope = useHasScope(scope)
  return <>{hasScope ? children : fallback}</>
}
